/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import { Analysis, CodeArticle, LinkDocument, ModelType, PublicationSheet } from '@livv/models';
import { getCollectionNameFromModelType } from '@livv/utils/helpers';
import { Tooltip } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { collection, doc, getDoc } from 'firebase/firestore';
import Link from 'next/link';
import { FC, ReactNode, useState } from 'react';
import PlainLink from '../PlainLink';
import TooltipContent, { TooltipContentProps } from './TooltipContent';
import { db } from '@config/firebase/client';
import useAmplitude from '@utils/hooks/useAmplitude';
import { sxs } from '@utils/mui/sx';
import { TrackingEventName } from '@utils/types/amplitude/events';

export type GetTooltipContent<T extends LinkDocument> = (data: T) => TooltipContentProps;

type GetUrl =
    | ((docId: string, bookDocument: CodeArticle | PublicationSheet) => string)
    | ((docId: string, analysis: Analysis) => string)
    | ((docId: string) => string);

interface CustomLinkProps {
    children: ReactNode;
    className?: string;
    dataTestId?: string;
    docId: string;
    getTooltipContentProps: GetTooltipContent<LinkDocument>;
    getUrl: GetUrl;
    modelType: ModelType;
    openInNewTab?: boolean;
    plainLink?: boolean;
    trackingEventName?: TrackingEventName;
    hideTooltip?: boolean;
    sx?: SxProps<Theme>;
    underline?: boolean;
}

const CustomLink: FC<CustomLinkProps> = ({
    children,
    className,
    dataTestId,
    docId,
    modelType,
    openInNewTab,
    plainLink = true,
    getTooltipContentProps,
    getUrl,
    trackingEventName,
    hideTooltip,
    sx = {},
    underline = true,
}) => {
    const collectionName = getCollectionNameFromModelType(modelType, docId);
    const [data, setData] = useState<LinkDocument | null | undefined>();
    const { track } = useAmplitude();
    const loadData = async () =>
        getDoc(doc(collection(db, collectionName), docId))
            .then((docRef) => setData(docRef.data() as LinkDocument))
            .catch((err) => {
                console.error(`error loading ${collectionName}/${docId} - ${err}`);
                setData(null);
            });
    const handleClick = () => {
        track({
            // TODO - VOG-944: Find a way to define trackingEventName as one of DefinedEvents name
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            name: trackingEventName,
            params: {
                target_document_id: docId,
                target_document_type: modelType,
            },
        });
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const finalHref = data ? getUrl(docId, data) : '';

    return (
        <Tooltip
            placement="bottom"
            title={data && !hideTooltip ? <TooltipContent {...getTooltipContentProps(data)} /> : ''}
        >
            <span
                onMouseOver={async () => {
                    if (data === undefined) {
                        loadData();
                    }
                }}
            >
                {plainLink ? (
                    <PlainLink
                        className={className}
                        dataTestId={dataTestId}
                        href={finalHref}
                        onClick={handleClick}
                        openInNewTab={openInNewTab}
                        sx={sxs([{ '&&': { color: 'inherit' } }, sx])}
                        underline={underline ? 'hover' : 'never'}
                    >
                        {children}
                    </PlainLink>
                ) : (
                    <Link
                        data-test-id={dataTestId}
                        href={finalHref}
                        onClick={handleClick}
                        target={openInNewTab ? '_blank' : undefined}
                    >
                        {children}
                    </Link>
                )}
            </span>
        </Tooltip>
    );
};

export default CustomLink;
